import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Auto-déconnexion par inactivité (« poste laissé ouvert »).
 *
 * Écoute l'activité utilisateur (souris/clavier/scroll/tactile, throttlée) et
 * réarme un minuteur à chaque action. À `timeoutMs - warnMs` d'inactivité,
 * bascule `warning` à vrai (afficher une modale « Déconnexion imminente »). À
 * `timeoutMs`, appelle `onIdle()` (la déconnexion). Toute activité — ou l'appel
 * explicite à `stayConnected()` — réarme les minuteurs et masque l'avertissement.
 *
 * Ne s'active que quand `enabled` est vrai (typiquement : utilisateur
 * authentifié). Les listeners sont nettoyés au démontage / quand `enabled`
 * repasse à faux.
 */
export interface UseIdleLogoutOptions {
  /** Inactivité totale avant déconnexion (défaut 15 min). */
  timeoutMs?: number;
  /** Fenêtre d'avertissement avant la déconnexion (défaut 60 s). */
  warnMs?: number;
  /** N'arme les minuteurs que si vrai (ex. utilisateur authentifié). */
  enabled?: boolean;
  /** Appelé une fois à l'expiration de l'inactivité (déconnexion). */
  onIdle: () => void;
}

export interface UseIdleLogoutResult {
  /** Vrai pendant la fenêtre d'avertissement (afficher la modale). */
  warning: boolean;
  /** Millisecondes restantes avant déconnexion (rafraîchi pendant l'avert.). */
  remainingMs: number;
  /** Réarme les minuteurs et masque l'avertissement (« Rester connecté »). */
  stayConnected: () => void;
}

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;
const DEFAULT_WARN_MS = 60 * 1000;
/** On ne réarme au plus qu'une fois par seconde (mousemove très bavard). */
const ACTIVITY_THROTTLE_MS = 1000;
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
  "touchstart",
];

export function useIdleLogout({
  timeoutMs = DEFAULT_TIMEOUT_MS,
  warnMs = DEFAULT_WARN_MS,
  enabled = true,
  onIdle,
}: UseIdleLogoutOptions): UseIdleLogoutResult {
  const [warning, setWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(warnMs);

  // Ref pour éviter une closure obsolète : `onIdle` peut changer d'identité
  // (ex. logout recréé à chaque render) sans devoir réarmer les minuteurs.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const warnTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const countdown = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const lastActivity = useRef(0);

  const clearAll = useCallback(() => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdown.current) clearInterval(countdown.current);
    warnTimer.current = undefined;
    idleTimer.current = undefined;
    countdown.current = undefined;
  }, []);

  const arm = useCallback(() => {
    clearAll();
    setWarning(false);
    setRemainingMs(warnMs);
    warnTimer.current = setTimeout(() => {
      setWarning(true);
      const deadline = Date.now() + warnMs;
      setRemainingMs(warnMs);
      countdown.current = setInterval(() => {
        setRemainingMs(Math.max(0, deadline - Date.now()));
      }, 1000);
    }, Math.max(0, timeoutMs - warnMs));
    idleTimer.current = setTimeout(() => {
      clearAll();
      onIdleRef.current();
    }, timeoutMs);
  }, [clearAll, timeoutMs, warnMs]);

  const stayConnected = useCallback(() => {
    arm();
  }, [arm]);

  useEffect(() => {
    if (!enabled) {
      clearAll();
      setWarning(false);
      return;
    }
    arm();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivity.current < ACTIVITY_THROTTLE_MS) return;
      lastActivity.current = now;
      arm();
    };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
      clearAll();
    };
  }, [enabled, arm, clearAll]);

  return { warning, remainingMs, stayConnected };
}
